'use client';

import React, { useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { YMapLocationRequest } from '@yandex/ymaps3-types/imperative/YMap';

import { IMapPlace } from 'app/interfaces/map';
import { useMap } from 'app/providers/mapProvider';
import { getBounds } from './helpers/getBounds';
import Marker from './Marker';
import Loading from './Loading';
import { style } from './style';

import Error from './Error';

interface IMapProps {
  places: IMapPlace[];
  activePlaceId?: string | number | null;
  onMarkerMouseEnter?(id: number | string): void;
  onMarkerMouseLeave?(id: number | string): void;
}

const defaultLocation = { center: [60.597474, 56.838011], zoom: 15 };

export default function Map({
  places,
  activePlaceId,
  onMarkerMouseEnter,
  onMarkerMouseLeave
}: IMapProps) {
  const { reactifyApi } = useMap();
  const location = useMemo<YMapLocationRequest>(() => {
    if (!places?.length) return defaultLocation;
    if (places.length === 1) return { center: places[0].coordinates, zoom: 16 };
    const bounds = getBounds(places.map(place => place.coordinates));
    return bounds ? { bounds } : defaultLocation;
  }, [places]);

  if (!reactifyApi) return <Loading />;

  const { YMap, YMapDefaultSchemeLayer, YMapDefaultFeaturesLayer } =
    reactifyApi;

  return (
    <ErrorBoundary fallback={<Error />}>
      <YMap
        margin={[80, 80, 80, 80]}
        location={location}
        behaviors={['pinchZoom', 'dblClick', 'drag', 'magnifier']}
      >
        <YMapDefaultSchemeLayer customization={style} />
        <YMapDefaultFeaturesLayer />
        {places.map((place: IMapPlace, index: number) => {
          const isActive = activePlaceId === place.id;
          return (
            <Marker
              key={index}
              id={place.id}
              coordinates={place.coordinates}
              reactifyApi={reactifyApi}
              isActive={isActive}
              onMouseEnter={onMarkerMouseEnter}
              onMouseLeave={onMarkerMouseLeave}
            />
          );
        })}
      </YMap>
    </ErrorBoundary>
  );
}
